import { EBusinessUnit } from '@aca-new/app/shared/models/enums/business-unit.enum';
import { AppUserSettingService } from '@aca-new/app/shared/services/user-services/app-user-setting/app-user-setting.service';
import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';

/**
 * @description
 * Only analytics module is available for produce BU
 * @param {RouterStateSnapshot} state the state
 * @returns {boolean} can access or not
 */
function produceGuard(state: RouterStateSnapshot): boolean {
  const producePaths = ['/analytics', '/produce-reports', '/account/access-credentials', '/account/delete-account'];

  if (producePaths.some((path): boolean => state.url.startsWith(path))) {
    return true;
  }

  const router = inject(Router);

  void router.navigate(['/analytics']);

  return false;
}

/**
 * @description
 * Only life science module is available for life science BUs
 * @param {RouterStateSnapshot} state the state
 * @returns {boolean} can access or not
 */
function lifeScienceGuard(state: RouterStateSnapshot): boolean {
  const lifeSciencePaths = ['/life-sciences'];

  if (lifeSciencePaths.some((path): boolean => state.url.startsWith(path))) {
    return true;
  }

  const router = inject(Router);

  void router.navigate(['/life-sciences']);

  return false;
}

/**
 * @description
 * Only analytics module, broker and home page is available for broker user
 * @param {RouterStateSnapshot} state the state
 * @returns {boolean} can access or not
 */
function brokerGuard(state: RouterStateSnapshot): boolean {
  const paths = [
    '/analytics',
    '/broker',
    '/html-report',
    '/interactive-report-v2-1',
    '/interactive-report-v2',
    '/home',
    '/account/my-documents',
    '/account/contact-details',
    '/account/access-credentials',
  ];

  if (paths.some((path): boolean => state.url.startsWith(path))) {
    return true;
  }

  const router = inject(Router);

  void router.navigate(['/broker']);

  return false;
}

/**
 * @description
 * Different roles have different access to the application
 * @param {ActivatedRouteSnapshot} _route {ActivatedRouteSnapshot} the route
 * @param {RouterStateSnapshot } state the state
 * @returns {boolean} - can access or not
 */
export default function businessUnitGuard(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
  const appUserSettingService = inject(AppUserSettingService);
  const businessUnit = appUserSettingService.getBusinessUnit();
  const isBroker = businessUnit && appUserSettingService.getIsBrokerType();
  const isLifeScience = appUserSettingService.getIsLifeScience();

  if (isBroker) {
    return brokerGuard(state);
  }

  if (isLifeScience) {
    return lifeScienceGuard(state);
  }

  switch (businessUnit) {
    case EBusinessUnit.QIMA_PRODUCE:
      return produceGuard(state);
    default:
      return true;
  }
}
